import { useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { transportationKeys } from '../../consts/conditions';
import { deepCopy } from '../../utils/deepCopy';
import { findAllObjects, removeObjectByIds, updateObjectById } from './useDefaultTransformation';

const _filterTypeItem = {
  id: uuidv4(),
  type: 'item',
  actionItem: 'condition', // | operator
  inputType: 'input',
  value: {
    input: '',
    column: '',
    null: null
  }
};

const _filterType = {
  id: uuidv4(),
  firstParent: true,
  label: transportationKeys.ARITHMETIC,
  type: 'group',
  then: {
    input: '',
    column: '',
    null: null
  },
  thenInputType: 'column',
  conditions: [
    {
      ...Object.assign({}, _filterTypeItem)
    }
  ]
};

export const useArithmetic = ({ setIsOpen, setSelectedTransformation, reset }) => {
  const [filterTypes, setFilterTypes] = useState<any[]>([]);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);

  useEffect(() => {
    return () => {
      setFilterTypes([]);
      setSelectedItems([]);
    };
  }, []);

  const onSelectFilterType = val => {
    setSelectedTransformation(null);

    setFilterTypes([
      {
        ...deepCopy(_filterType),
        label: val,
        isNew: true,
        isParent: true,
        conditions: [
          {
            ...deepCopy(_filterTypeItem),
            id: uuidv4(),
            inputType: 'column',
            value: { input: null, column: null, null: null }
          },
          {
            ...deepCopy(_filterTypeItem),
            id: uuidv4(),
            actionItem: 'operator',
            inputType: 'column',
            value: { input: null, column: null, null: null }
          },
          {
            ...deepCopy(_filterTypeItem),
            id: uuidv4(),
            inputType: 'column',
            value: { input: null, column: null, null: null }
          }
        ]
      }
    ]);

    setIsOpen(false);
  };

  const getAllConditionsWithActiveFilterType = filterTypeId => {
    let _filterTypes = [...filterTypes];

    const _selectedItems = [...selectedItems];

    const activeFilterType = _filterTypes.find(type => type.id === filterTypeId);

    const allFlats = findAllObjects(activeFilterType);

    return { _filterTypes, allFlats, _selectedItems, activeFilterType };
  };

  const onGrouping = (parentId, filterTypeId) => {
    const { _filterTypes, allFlats, _selectedItems, activeFilterType } =
      getAllConditionsWithActiveFilterType(filterTypeId);

    const parentIdsOfSelectedItems = allFlats
      .filter(
        el =>
          el.conditions?.length > 0 &&
          selectedItems.find(itemId => el.conditions.find(condition => condition.id === itemId))
      )
      .map(el => el.id);

    const currentParentId =
      (parentIdsOfSelectedItems.includes(parentId) ? parentId : parentIdsOfSelectedItems?.[0]) ||
      parentId;

    let foundItem = allFlats.find(item => item.id === currentParentId);

    const sortSelectedItems = (_selectedItems, foundItem) => {
      return [..._selectedItems].sort((a, b) => {
        // Get the index of each UUID in foundItem.conditions
        const indexA = foundItem.conditions.findIndex(el => el.id === a);
        const indexB = foundItem.conditions.findIndex(el => el.id === b);

        // Ensure items not in conditions move to the end
        return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB);
      });
    };

    const groupBetweenConditions = () => {
      const newId = uuidv4();
      const insertIndex = foundItem.conditions.findIndex(el => el.id === _selectedItems[0]);

      const sortedItems = [...sortSelectedItems(_selectedItems, foundItem)];

      const groupedItems = sortedItems.reduce((acc, id, index) => {
        const item = {
          ...allFlats.find(el => el.id === id),
          id: uuidv4()
        };

        const itemIndex = foundItem.conditions.findIndex(el => el.id === id);

        if (itemIndex > 0 && index > 0) {
          const operator = {
            ...deepCopy(foundItem.conditions[itemIndex - 1])
          };

          removeObjectByIds(_filterTypes, [operator.id]);

          acc.push({
            ...deepCopy(operator),
            actionItem: 'operator',
            id: uuidv4()
          });
        }

        acc.push(item);

        return acc;
      }, []);

      foundItem.conditions.splice(insertIndex, 0, {
        ...deepCopy(_filterType),
        id: newId,
        type: 'group',
        firstParent: true,
        label: activeFilterType.label,
        conditions: [...groupedItems]
      });

      let updatedFilterTypes = removeObjectByIds(_filterTypes, _selectedItems);

      updateObjectById(updatedFilterTypes, foundItem.id, { ...foundItem });
      setFilterTypes(updatedFilterTypes);
      setSelectedItems([]);
    };

    const newGroup = () => {
      const newId = uuidv4();

      onAdding(currentParentId, filterTypeId, true);

      foundItem.conditions.push({
        ...deepCopy(_filterType),
        id: newId,
        type: 'group',
        firstParent: true,
        label: activeFilterType.label,
        conditions: [
          {
            ...deepCopy(_filterTypeItem),
            id: uuidv4()
          }
        ]
      });

      updateObjectById(_filterTypes, foundItem.id, { ...foundItem });
      setFilterTypes(_filterTypes);
    };

    if (foundItem?.firstParent && selectedItems?.length > 0) return groupBetweenConditions();

    if (foundItem?.firstParent) return newGroup();

    onAdding(currentParentId, filterTypeId, true);

    if (foundItem)
      foundItem.conditions = [
        ...foundItem.conditions,
        {
          ...deepCopy(_filterType),
          id: uuidv4(),
          type: 'group',
          label: activeFilterType.label,
          conditions: [
            {
              ...deepCopy(_filterTypeItem),
              id: uuidv4()
            }
          ]
        }
      ];

    let updatedFilterTypes = removeObjectByIds(_filterTypes, _selectedItems);

    updateObjectById(updatedFilterTypes, foundItem.id, { ...foundItem });
    setFilterTypes(updatedFilterTypes);
    setSelectedItems([]);
  };

  const onAdding = (parentId, filterTypeId, onlyOperator = false) => {
    const { _filterTypes, allFlats } = getAllConditionsWithActiveFilterType(filterTypeId);

    let foundItem = allFlats.find(item => item.id === parentId);

    if (onlyOperator)
      return foundItem.conditions?.push({
        ...deepCopy(_filterTypeItem),
        actionItem: 'operator',
        id: uuidv4()
      });

    foundItem.conditions?.push({
      ...deepCopy(_filterTypeItem),
      actionItem: 'operator',
      id: uuidv4()
    });

    foundItem.conditions?.push({
      ...deepCopy(_filterTypeItem),
      actionItem: 'condition',
      id: uuidv4()
    });

    updateObjectById(_filterTypes, foundItem.id, { ...foundItem });
    setFilterTypes(_filterTypes);
  };

  const onChangeInput = (filterTypeId, itemId, val) => {
    const { _filterTypes, allFlats } = getAllConditionsWithActiveFilterType(filterTypeId);

    let foundItem = allFlats.filter(el => el).find(item => item?.id === itemId);

    if (foundItem) foundItem.value[foundItem.inputType] = val;

    updateObjectById(_filterTypes, foundItem?.id, { ...foundItem });
    setFilterTypes(_filterTypes);
  };

  const onChangeThen = (filterTypeId, itemId, val) => {
    const { _filterTypes, allFlats } = getAllConditionsWithActiveFilterType(filterTypeId);

    let foundItem = allFlats.find(item => item.id === itemId);

    foundItem.then[foundItem.thenInputType] = val;

    updateObjectById(_filterTypes, foundItem.id, { ...foundItem });
    setFilterTypes(_filterTypes);
  };

  const onChangeThenInputType = (filterTypeId, itemId, val) => {
    const { _filterTypes, allFlats } = getAllConditionsWithActiveFilterType(filterTypeId);

    let foundItem = allFlats.find(item => item.id === itemId);

    foundItem.thenInputType = val;

    updateObjectById(_filterTypes, foundItem.id, { ...foundItem });
    setFilterTypes(_filterTypes);
  };

  const addCondition = (id, type, filterTypeId) => {
    if (type === 'group') onGrouping(id, filterTypeId);
    else onAdding(id, filterTypeId);
  };

  const onSelectCondition = (filterTypeId, value, itemId) => {
    const { _filterTypes, allFlats } = getAllConditionsWithActiveFilterType(filterTypeId);

    let foundItem = allFlats.find(item => item.id === itemId);
    foundItem.conditionLabel = value;

    updateObjectById(_filterTypes, foundItem.id, { ...foundItem });
    setFilterTypes(_filterTypes);
  };

  const onSelectItem = itemId => {
    if (selectedItems.includes(itemId)) setSelectedItems(prev => prev.filter(el => el !== itemId));
    else setSelectedItems(prev => [...prev, itemId]);
  };

  const onDeleteSelectedItems = (filterTypeId, removabledItemsId) => {
    const { _filterTypes, _selectedItems, allFlats } =
      getAllConditionsWithActiveFilterType(filterTypeId);
    const _updatedRemovalbleItemsId = [];

    _selectedItems.forEach(id => {
      _updatedRemovalbleItemsId.push(id);

      const index = allFlats.findIndex(item => item.id === id);
      if (index > 0) _updatedRemovalbleItemsId.push(allFlats[index - 1].id);
    });

    let updatedFilterTypes = _updatedRemovalbleItemsId
      ? removeObjectByIds(_filterTypes, _updatedRemovalbleItemsId)
      : removeObjectByIds(_filterTypes, _selectedItems);

    setFilterTypes(updatedFilterTypes);
    setSelectedItems([]);
  };

  const onAddFilterType = isChild => {
    if (isChild) setFilterTypes(prev => [...prev, { ...deepCopy(_filterTypeItem), id: uuidv4() }]);
    else
      setFilterTypes(prev => [
        ...prev,
        {
          ...deepCopy(_filterType),
          id: uuidv4(),
          label: prev?.[0]?.value,
          conditions: [{ ...deepCopy(_filterTypeItem), id: uuidv4() }]
        }
      ]);
  };

  const onChangeInputType = (filterTypeId, itemId, val) => {
    const { _filterTypes, allFlats } = getAllConditionsWithActiveFilterType(filterTypeId);

    let foundItem = allFlats.find(item => item.id === itemId);

    foundItem.inputType = val;

    updateObjectById(_filterTypes, foundItem.id, { ...foundItem });
    setFilterTypes(_filterTypes);
  };

  const onChangeElseValue = (filterTypeId, itemId, val) => {
    const { _filterTypes, allFlats } = getAllConditionsWithActiveFilterType(filterTypeId);

    let foundItem = allFlats.find(item => item.id === itemId);

    foundItem.elseValue[foundItem.elseInputType] = val;

    updateObjectById(_filterTypes, foundItem.id, { ...foundItem });
    setFilterTypes(_filterTypes);
  };

  const onChangeReplaceColumn = (filterTypeId, itemId, val) => {
    const { _filterTypes, allFlats } = getAllConditionsWithActiveFilterType(filterTypeId);

    let foundItem = allFlats.find(item => item.id === itemId);

    foundItem.replaceColumn = val;

    updateObjectById(_filterTypes, foundItem.id, { ...foundItem });
    setFilterTypes(_filterTypes);
  };

  const onChangeElseInputType = (filterTypeId, itemId, val) => {
    const { _filterTypes, allFlats } = getAllConditionsWithActiveFilterType(filterTypeId);

    let foundItem = allFlats.find(item => item.id === itemId);

    foundItem.elseInputType = val;

    updateObjectById(_filterTypes, foundItem.id, { ...foundItem });
    setFilterTypes(_filterTypes);
  };

  const disableSaveBtn = useMemo(() => {
    return false;
  }, [filterTypes]);

  const checkForRequiredFields = () => {
    let hasEmptyField = false;

    filterTypes.forEach((filterType, fIdx) => {
      const { _filterTypes, allFlats } = getAllConditionsWithActiveFilterType(filterType.id);
      let hasResultValue = false;

      allFlats.forEach(item => {
        const errors = [];

        if (!item.value?.column && !item.value?.input && item.actionItem === 'condition') {
          errors.push('condition');
        }

        if (!item.then?.column && !item.then?.input && item.type === 'group' && !hasResultValue) {
          errors.push('then');
        } else {
          hasResultValue = true;
        }

        if (
          item.type === 'item' &&
          !item.value.column &&
          !item.value.input &&
          item.actionItem === 'operator'
        ) {
          errors.push('operator');
        }

        if (errors.length > 0) hasEmptyField = true;

        item.errors = errors;

        updateObjectById(_filterTypes, item.id, { ...item });
      });

      setFilterTypes(_filterTypes);
    });

    return hasEmptyField;
  };

  const clear = () => {
    setFilterTypes([]);
    setSelectedItems([]);
    reset();
  };

  return {
    filterTypes,
    selectedItems,
    disableSaveBtn,
    clear,
    onChangeThen,
    addCondition,
    onSelectItem,
    onChangeInput,
    handleDragEnd: () => {},
    onChangeColumn: () => {},
    setFilterTypes,
    onAddFilterType,
    onChangeOperator: () => {},
    onChangeFunction: () => {},
    onChangeInputType,
    onSelectCondition,
    onChangeElseValue,
    onToggleColumnItem: () => {},
    onSelectFilterType,
    onChangeReplaceColumn,
    onChangeElseInputType,
    onDeleteSelectedItems,
    onChangeThenInputType,
    checkForRequiredFields
  };
};
